import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from 'helpers/redux';
import { RootState } from 'app/store';
import {
    fetchCatalogDetail,
    approveCatalogStatus,
    fetchVersionHistory,
    deleteCatalogAction,
} from '../catalog.reducer';
import {
    CATALOG_CATEGORY_MAP,
    CATALOG_STATUS_MAP,
    CatalogItem,
} from '../models';
import { downloadFileWithFallback } from 'helpers/downloadFile';
import CatalogVersionHistory from '../components/CatalogVersionHistory';
import UploadNewVersionModal from '../components/UploadNewVersionModal';
import EditCatalogDescriptionModal from '../components/EditCatalogDescriptionModal';
import {
    RiArrowLeftLine,
    RiFileTextLine,
    RiDownload2Line,
    RiCheckDoubleLine,
    RiTimeLine,
    RiFolder2Line,
    RiMapPinLine,
    RiUser3Line,
    RiInformationLine,
    RiHistoryLine,
    RiDeleteBinLine,
    RiEdit2Line,
} from 'react-icons/ri';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { UserCredential } from 'features/login/models';

const CatalogDetailPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const MySwal = withReactContent(Swal);

    const singleItem = useAppSelector(
        (state: RootState) => state.catalog.single,
    );
    const catalogList = useAppSelector(
        (state: RootState) => state.catalog.list ?? [],
    );
    const versionHistory = useAppSelector(
        (state: RootState) => state.catalog.versionHistory ?? [],
    );
    const isLoading = useAppSelector(
        (state: RootState) => state.catalog.isLoading ?? false,
    );
    const user = useAppSelector(
        (state: RootState) => state.user.userAuth as UserCredential,
    );

    const canApprove =
        user?.roleName?.some(
            (r) =>
                r.toUpperCase().includes('HEAD') ||
                r.toLowerCase() === 'superadmin',
        ) || false;

    const [isVersionModalOpen, setIsVersionModalOpen] =
        useState<boolean>(false);
    const [isEditDescModalOpen, setIsEditDescModalOpen] =
        useState<boolean>(false);
    const canUpload =
        user?.roleName?.some(
            (r) =>
                r.toUpperCase().includes('SPV') ||
                r.toLowerCase() === 'superadmin',
        ) || false;

    // Use singleItem or fallback to finding in list
    const item =
        singleItem?.id === id
            ? singleItem
            : catalogList.find((i) => i.id === id);

    useEffect(() => {
        if (id) {
            if (!item || item.id !== id) {
                dispatch(fetchCatalogDetail(id));
            }
            dispatch(fetchVersionHistory(id));
        }
    }, [id, item?.id, dispatch]);

    const handleApprove = () => {
        if (!item) return;
        MySwal.fire({
            title: 'Approve & Publish Catalog?',
            text: `Are you sure to publish this catalog?`,
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#10B981',
            cancelButtonColor: '#6B7280',
            confirmButtonText: 'Yes, Publish!',
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(approveCatalogStatus(item.id)).then(() => {
                    if (id) {
                        dispatch(fetchCatalogDetail(id));
                        dispatch(fetchVersionHistory(id));
                    }
                });
                MySwal.fire(
                    'Published!',
                    'The catalog document has been successfully published.',
                    'success',
                );
            }
        });
    };

    const handleDelete = () => {
        if (!item) return;
        MySwal.fire({
            title: 'Delete Catalog?',
            text: `Are you sure you want to delete this catalog? This action cannot be undone.`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#EF4444',
            cancelButtonColor: '#6B7280',
            confirmButtonText: 'Yes, Delete!',
        }).then((result) => {
            if (result.isConfirmed) {
                dispatch(deleteCatalogAction(item.id)).then(() => {
                    navigate('/catalog');
                });
                MySwal.fire(
                    'Deleted!',
                    'The catalog document has been successfully deleted.',
                    'success',
                );
            }
        });
    };

    const handleDownload = (targetItem?: CatalogItem) => {
        const doc = targetItem || item;
        if (!doc || !doc.file) return;
        const dlName =
            doc.originalFileName || doc.file.split('/').pop() || 'document';
        downloadFileWithFallback(doc.file, dlName);
    };

    if (isLoading && !item) {
        return (
            <div className="p-12 text-center min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4c8b6a] mx-auto"></div>
            </div>
        );
    }

    if (!item) {
        return (
            <div className="p-8 md:p-12 max-w-4xl mx-auto text-center min-h-screen bg-gray-50 dark:bg-gray-900">
                <div className="bg-white dark:bg-gray-800 p-8 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700">
                    <h2 className="text-xl font-bold text-gray-800 dark:text-white mb-2">
                        Document Not Found
                    </h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                        The requested catalog document (ID: {id}) could not be
                        found or has been removed.
                    </p>
                    <button
                        onClick={() => navigate('/catalog')}
                        className="inline-flex items-center px-5 py-2.5 bg-[#4c8b6a] hover:bg-[#3d7055] text-white font-medium text-sm rounded-lg shadow-sm transition-colors"
                    >
                        <RiArrowLeftLine className="mr-2" />
                        Back to Catalog List
                    </button>
                </div>
            </div>
        );
    }

    const isPending = versionHistory.some((v) => v.status === 0);
    const isPublished = item.status === 1;

    const hasDraftVersion =
        item.status === 0 || versionHistory.some((v) => v.status === 0);

    return (
        <div className="p-4 md:p-8 bg-gray-50/50 dark:bg-gray-900 min-h-screen">
            {/* Top Back Nav & Quick Info */}
            <div className="mb-6 flex items-center justify-between">
                <button
                    onClick={() => navigate('/catalog')}
                    className="inline-flex items-center text-sm font-semibold text-gray-600 dark:text-gray-300 hover:text-[#4c8b6a] dark:hover:text-[#4c8b6a] transition-colors bg-white dark:bg-gray-800 px-4 py-2 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm"
                >
                    <RiArrowLeftLine className="mr-2" size={18} />
                    Back to Catalog
                </button>

                <div className="flex items-center space-x-3">
                    {isPending && canApprove && (
                        <button
                            onClick={handleApprove}
                            className="inline-flex items-center px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm rounded-xl shadow-md hover:shadow-lg transition-all animate-bounce-short"
                        >
                            <RiCheckDoubleLine className="mr-1.5 text-lg" />
                            Approve & Publish (Head)
                        </button>
                    )}

                    {canApprove && (
                        <button
                            onClick={handleDelete}
                            className="inline-flex items-center px-4 py-2 bg-red-50 hover:bg-red-100 text-red-600 font-bold text-sm rounded-xl shadow-sm hover:shadow-md transition-all"
                        >
                            <RiDeleteBinLine className="mr-1.5 text-lg" />
                            Delete
                        </button>
                    )}

                    <button
                        onClick={() => handleDownload()}
                        className="inline-flex items-center px-4 py-2 bg-[#4c8b6a] hover:bg-[#3d7055] text-white font-medium text-sm rounded-lg shadow-sm transition-colors"
                    >
                        <RiDownload2Line className="mr-1.5 text-lg" />
                        Download Latest Publish
                        {/* ({item.file?.split('/').pop() || 'document'}) */}
                    </button>

                    {canUpload && (
                        <button
                            onClick={() => {
                                if (hasDraftVersion) {
                                    MySwal.fire({
                                        title: 'Cannot Upload',
                                        text: 'A draft version is currently pending. Please wait for it to be approved or rejected before uploading a new version.',
                                        icon: 'warning',
                                        confirmButtonColor: '#4c8b6a',
                                    });
                                } else {
                                    setIsVersionModalOpen(true);
                                }
                            }}
                            className={`inline-flex items-center px-4 py-2 font-medium text-sm rounded-lg shadow-sm transition-colors ${
                                hasDraftVersion
                                    ? 'bg-gray-200 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
                                    : 'bg-[#4c8b6a] hover:bg-[#3d7055] text-white'
                            }`}
                        >
                            <RiHistoryLine className="mr-1.5 text-lg" />+ Upload
                            New Version
                        </button>
                    )}
                </div>
            </div>

            {/* Document Title Header Banner */}
            <div className="bg-white dark:bg-gray-800 p-6 md:p-8 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm mb-6">
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                    <div className="flex items-start space-x-4">
                        <div className="w-14 h-14 rounded-2xl bg-[#4c8b6a]/10 dark:bg-[#4c8b6a]/20 text-[#4c8b6a] flex items-center justify-center text-3xl shrink-0 shadow-inner">
                            <RiFileTextLine />
                        </div>
                        <div>
                            <div className="flex flex-wrap items-center gap-2 mb-2">
                                <span className="px-2.5 py-0.5 rounded-full bg-[#4c8b6a]/10 text-[#4c8b6a] font-bold text-xs border border-[#4c8b6a]/30">
                                    {CATALOG_CATEGORY_MAP[item.category] ||
                                        'SOP'}
                                </span>
                                <span className="px-2 py-0.5 rounded-md bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 font-bold text-xs">
                                    {item.version}
                                </span>
                                <span
                                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                                        isPublished
                                            ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300 border border-emerald-200'
                                            : 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300 border border-amber-200 animate-pulse'
                                    }`}
                                >
                                    <span
                                        className={`w-2 h-2 rounded-full mr-1.5 ${
                                            isPublished
                                                ? 'bg-emerald-500'
                                                : 'bg-amber-500'
                                        }`}
                                    />
                                    {CATALOG_STATUS_MAP[item.status] ||
                                        'Unknown'}
                                </span>
                            </div>
                            <h1 className="text-2xl font-extrabold text-gray-800 dark:text-white leading-tight">
                                {item.title}
                            </h1>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                {item.originalFileName ||
                                    item.file?.split('/').pop() ||
                                    'document'}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Grid Layout: Metadata & Version History */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                {/* Left Col: Document Info & Metadata */}
                <div className="lg:col-span-5 space-y-6">
                    <div className="bg-white dark:bg-gray-800 p-6 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm">
                        <h3 className="text-base font-bold text-gray-800 dark:text-white mb-4 flex items-center border-b border-gray-100 dark:border-gray-700 pb-3">
                            <RiInformationLine
                                className="mr-2 text-[#4c8b6a]"
                                size={20}
                            />
                            Document Metadata
                        </h3>

                        <div className="space-y-4 text-sm">
                            <div className="flex items-start justify-between">
                                <span className="text-gray-400 flex items-center">
                                    <RiFolder2Line className="mr-2 text-gray-400" />{' '}
                                    Category
                                </span>
                                <span className="font-semibold text-gray-800 dark:text-gray-200">
                                    {CATALOG_CATEGORY_MAP[item.category] ||
                                        'SOP'}
                                </span>
                            </div>

                            <div className="flex items-start justify-between">
                                <span className="text-gray-400 flex items-center">
                                    <RiMapPinLine className="mr-2 text-gray-400" />{' '}
                                    Team
                                </span>
                                <span className="font-semibold text-gray-800 dark:text-gray-200">
                                    {item.team || item.teamId || '-'}
                                </span>
                            </div>

                            <div className="flex items-start justify-between">
                                <span className="text-gray-400 flex items-center">
                                    <RiUser3Line className="mr-2 text-gray-400" />{' '}
                                    Created By
                                </span>
                                <div className="text-right">
                                    <span className="font-semibold text-gray-800 dark:text-gray-200 block">
                                        {item.createby_id_name ||
                                            item.createdby_name ||
                                            'System User'}
                                    </span>
                                </div>
                            </div>

                            <div className="flex items-start justify-between">
                                <span className="text-gray-400 flex items-center">
                                    <RiTimeLine className="mr-2 text-gray-400" />{' '}
                                    Date Created
                                </span>
                                <span className="font-semibold text-gray-800 dark:text-gray-200">
                                    {new Date(
                                        item.ts_created || Date.now(),
                                    ).toLocaleString('en-GB')}
                                </span>
                            </div>

                            {/* <div className="flex items-start justify-between">
                                <span className="text-gray-400 flex items-center">
                                    <RiUser3Line className="mr-2 text-gray-400" />{' '}
                                    Last Updated By
                                </span>
                                <span className="font-semibold text-gray-800 dark:text-gray-200">
                                    {item.modifiedby_name ||
                                        item.modifiedby ||
                                        '-'}
                                </span>
                            </div> */}
                        </div>

                        {/* Description Box */}
                        <div className="mt-6 pt-4 border-t border-gray-100 dark:border-gray-700">
                            <div className="flex items-center justify-between mb-2">
                                <label className="block text-xs font-bold uppercase tracking-wider text-gray-400">
                                    Description / History Perubahan
                                </label>
                                {canUpload && (
                                    <button
                                        onClick={() => setIsEditDescModalOpen(true)}
                                        className="text-xs font-semibold text-[#4c8b6a] hover:text-[#3d7055] transition-colors flex items-center"
                                    >
                                        <RiEdit2Line className="mr-1" /> Edit
                                    </button>
                                )}
                            </div>
                            <div className="p-3.5 bg-gray-50 dark:bg-gray-700/50 rounded-xl text-sm text-gray-700 dark:text-gray-300 border border-gray-100 dark:border-gray-700 leading-relaxed">
                                {item.description || 'No description provided.'}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Col: Version History Timeline */}
                <div className="lg:col-span-7">
                    <div className="bg-white dark:bg-gray-800 p-6 md:p-8 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm">
                        <CatalogVersionHistory
                            versions={
                                versionHistory.length > 0
                                    ? versionHistory
                                    : item.versions || []
                            }
                            onDownloadVersion={(ver) => handleDownload(ver)}
                        />
                    </div>
                </div>
            </div>

            {/* Upload New Version Modal */}
            <UploadNewVersionModal
                isOpen={isVersionModalOpen}
                onClose={() => setIsVersionModalOpen(false)}
                item={item}
                onSuccess={() => {
                    if (id) {
                        dispatch(fetchCatalogDetail(id));
                        dispatch(fetchVersionHistory(id));
                    }
                }}
            />
            {/* Edit Description Modal */}
            <EditCatalogDescriptionModal
                isOpen={isEditDescModalOpen}
                onClose={() => setIsEditDescModalOpen(false)}
                item={item}
                onSuccess={() => {
                    if (id) {
                        dispatch(fetchCatalogDetail(id));
                        dispatch(fetchVersionHistory(id));
                    }
                }}
            />
        </div>
    );
};

export default CatalogDetailPage;
